<template>
    <div class="salesforce-activity-settings mt-4">
        <h5 class="text-black">Activity Settings</h5>
        <div class="mb-4">
            <!-- Section header -->
            <div class="d-flex align-items-center justify-content-center mb-3">
                <strong>CDR / MDR Property Mapping</strong>
                <el-tooltip class="item ml-2" effect="dark" placement="right">
                    <div slot="content">
                        <b>CDR</b> — Call Detail Record: metadata about a phone call (duration, time, recording).<br>
                        <b>MDR</b> — Message Detail Record: metadata about an SMS/MMS message.<br><br>
                        Map these Aloware fields to custom Salesforce Activity fields.<br>
                        Each mapped field will be written to the Salesforce Task when a call or message is logged.
                    </div>
                    <i class="material-icons text-lg cursor-pointer text-blue p-0" style="font-size: 18px;">info_outline</i>
                </el-tooltip>
            </div>

            <!-- Column headers (shown only when there is at least one row) -->
            <div class="row form-group" v-if="model.settings.communication_property_mapping.length > 0">
                <div class="col-md-5">
                    <small class="font-weight-bold">Aloware Field</small>
                </div>
                <div class="col-md-2 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="col-md-4">
                    <small class="font-weight-bold">Salesforce Activity Field</small>
                </div>
            </div>

            <!-- Mapping rows -->
            <div
                v-for="(mapping, index) in model.settings.communication_property_mapping"
                :key="index"
                class="row form-group mt-2"
            >
                <!-- Aloware CDR/MDR field -->
                <div class="col-md-5 mb-1">
                    <el-select
                        v-model="mapping.aloware_field"
                        placeholder="Select Aloware field"
                        class="w-full"
                        filterable
                    >
                        <el-option
                            v-for="field in availableAlowareFields(index)"
                            :key="field.value"
                            :label="field.label"
                            :value="field.value"
                        >
                            {{ field.label }}
                        </el-option>
                    </el-select>
                </div>

                <!-- Direction indicator (always outbound: Aloware → Salesforce) -->
                <div class="col-md-2 text-center" style="padding-top: 8px;">
                    <i class="fa fa-arrow-right text-grey-5" style="font-size: 16px;"></i>
                </div>

                <!-- Salesforce Activity field -->
                <div class="col-md-4 mb-1">
                    <el-select
                        v-model="mapping.sf_field"
                        placeholder="Select Salesforce field"
                        class="w-full"
                        :loading="isLoadingActivityFields"
                        filterable
                    >
                        <el-option
                            v-for="field in availableSfFields(index)"
                            :key="field.name"
                            :label="field.label"
                            :value="field.name"
                        >
                            <span>{{ field.label }} <small class="text-muted">({{ field.name }})</small></span>
                            <el-tag size="mini" type="info" class="ml-2" style="font-size: 10px;">{{ field.type }}</el-tag>
                        </el-option>
                    </el-select>
                </div>

                <!-- Delete button -->
                <div class="col-md-1 text-center">
                    <el-button
                        size="small"
                        circle
                        plain
                        @click="removeCommunicationMapping(index)"
                    >
                        <i class="fa fa-trash"></i>
                    </el-button>
                </div>
            </div>

            <!-- Empty state -->
            <div v-if="model.settings.communication_property_mapping.length === 0" class="row form-group">
                <div class="col-md-12 text-center text-muted py-2">
                    <small>No mappings configured yet.</small>
                </div>
            </div>

            <!-- Add Mapping button -->
            <div class="row form-group">
                <div class="col-md-11 text-center">
                    <el-button
                        size="small"
                        :disabled="model.settings.communication_property_mapping.length >= cdrAlowareFields.length"
                        @click="addCommunicationMapping"
                    >
                        <i class="fa fa-plus-circle"></i>
                        <span class="ml-1 font-weight-bold">ADD MAPPING</span>
                    </el-button>
                </div>
            </div>
        </div>
        <table class="table subject-settings-table">
            <thead>
            <tr>
                <td colspan="2" class="text-center">
                    <div class="section-header">
                        <strong>Subject Format</strong>
                        <el-tooltip class="item" effect="dark" placement="right">
                            <div slot="content">
                                When a task is created, the subject line will be dynamically generated using the selected fields.
                            </div>
                            <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                        </el-tooltip>
                    </div>
                </td>
            </tr>
            <tr>
                <th class="text-center">Call</th>
                <th class="text-center">SMS</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>
                    <draggable 
                        v-model="sortedCallSubjectFields" 
                        class="sortable-checkbox-group vertical-checkboxes"
                        @end="onDragEnd('call')"
                        handle=".drag-handle"
                        ghost-class="ghost"
                        chosen-class="chosen"
                        drag-class="dragging"
                    >
                        <div 
                            v-for="field in sortedCallSubjectFields" 
                            :key="field.value" 
                            class="sortable-checkbox-item"
                        >
                            <el-checkbox 
                                v-model="model.settings.activities_settings.call" 
                                :label="field.value"
                            >
                                {{ field.label }}
                                <el-tooltip class="item" effect="dark" placement="top">
                                    <div slot="content">{{ field.description || '' }}</div>
                                    <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                </el-tooltip>
                            </el-checkbox>
                            <div class="drag-handle" title="Drag to reorder">
                                <i class="material-icons text-gray">unfold_more</i>
                            </div>
                        </div>
                    </draggable>
                </td>
                <td>
                    <draggable 
                        v-model="sortedSmsSubjectFields" 
                        class="sortable-checkbox-group vertical-checkboxes"
                        @end="onDragEnd('sms')"
                        handle=".drag-handle"
                        ghost-class="ghost"
                        chosen-class="chosen"
                        drag-class="dragging"
                    >
                        <div 
                            v-for="field in sortedSmsSubjectFields" 
                            :key="field.value" 
                            class="sortable-checkbox-item"
                        >
                            <el-checkbox 
                                v-model="model.settings.activities_settings.sms" 
                                :label="field.value"
                            >
                                {{ field.label }}
                                <el-tooltip class="item" effect="dark" placement="top">
                                    <div slot="content">{{ field.description || '' }}</div>
                                    <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                </el-tooltip>
                            </el-checkbox>
                            <div class="drag-handle" title="Drag to reorder">
                                <i class="material-icons text-gray">unfold_more</i>
                            </div>
                        </div>
                    </draggable>
                </td>
            </tr>
            </tbody>
        </table>
        <table class="table description-settings-table">
            <thead>
            <tr>
                <td colspan="2" class="text-center">
                    <div class="section-header">
                        <strong>Description Settings</strong>
                        <el-tooltip class="item" effect="dark" placement="right">
                            <div slot="content">
                                When a task is created, the description will be dynamically generated using the selected fields.
                            </div>
                            <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                        </el-tooltip>
                    </div>
                </td>
            </tr>
            <tr>
                <th class="text-center">Call</th>
                <th class="text-center">SMS</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.custom_activity_description_sections.call">
                        <el-checkbox v-for="field in callDescriptionFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.custom_activity_description_sections.sms">
                        <el-checkbox v-for="field in smsDescriptionFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
import draggable from 'vuedraggable'
import axios from 'axios'

export default {
    components: {
        draggable
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            fieldOrders: {
                call: [],
                sms: []
            },
            activityFields: [],
            isLoadingActivityFields: false,
        }
    },
    created() {
      if (!this.model.settings.activities_settings) {
        this.$set(this.model.settings, 'activities_settings', {})
      }

      if (!this.model.settings.activities_settings.call || this.model.settings.activities_settings.call.length === 0) {
        // Default for Call
        this.$set(this.model.settings.activities_settings, 'call', ['call_disposition', 'to'])
      }

      if (!this.model.settings.activities_settings.sms || this.model.settings.activities_settings.sms.length === 0) {
        // Default for SMS
        this.$set(this.model.settings.activities_settings, 'sms', ['from', 'communication_direction', 'to'])
      }

      if (!this.model.settings.custom_activity_description_sections) {
        this.$set(this.model.settings, 'custom_activity_description_sections', {})
      }

      if (!this.model.settings.custom_activity_description_sections.call || this.model.settings.custom_activity_description_sections.call.length === 0) {
        // Default for Call
        this.$set(this.model.settings.custom_activity_description_sections, 'call', this.callDescriptionFields.map(field => field.value))
      }

      if (!this.model.settings.custom_activity_description_sections.sms || this.model.settings.custom_activity_description_sections.sms.length === 0) {
        // Default for SMS
        this.$set(this.model.settings.custom_activity_description_sections, 'sms', this.smsDescriptionFields.map(field => field.value))
      }

      if (!Array.isArray(this.model.settings.communication_property_mapping)) {
        this.$set(this.model.settings, 'communication_property_mapping', [])
      }

      this.fetchActivityFields()

      // Get the saved order - if we have a saved order, use it; otherwise use default
      let savedOrder = this.model.settings.activities_settings?.call_order || []
      if (savedOrder.length > 0) {
        // Use the saved full order (including unchecked elements)
        this.fieldOrders.call = [...savedOrder]
      } else {
        this.fieldOrders.call = this.callSubjectFields.map(field => field.value)
      }

      // Get the saved order - if we have a saved order, use it; otherwise use default
      savedOrder = this.model.settings.activities_settings?.sms_order || []
      if (savedOrder.length > 0) {
        // Use the saved full order (including unchecked elements)
        this.fieldOrders.sms = [...savedOrder]
      } else {
        this.fieldOrders.sms = this.smsSubjectFields.map(field => field.value)
      }
  },
  computed: {
        callDescriptionFields() {
            return [
                { value: "call_completed", label: "Call Completed", description: "Date/Time that the call was completed" },
                { value: "call_outcome", label: "Call Outcome", description: "Result/Outcome of the call" },
                { value: "call_duration", label: "Call Duration", description: "Length of the call" },
                { value: "call_direction", label: "Call Direction", description: "Direction of the call (Inbound/Outbound)" },
                { value: "line", label: "Line", description: "Phone line used" },
                { value: "from", label: "From", description: "Caller information" },
                { value: "to", label: "To", description: "Call recipient" },
                { value: "recording_url", label: "Recording URL", description: "Link to the call recording" },
                { value: "voicemail_url", label: "Voicemail URL", description: "Link to the voicemail" },
                { value: "tags", label: "Tags", description: "Tags associated with the call" },
                { value: "notes", label: "Notes", description: "Additional notes" },
                { value: "source", label: "Source", description: "The source of the call" },
            ];
        },
        smsDescriptionFields() {
            return [
                { value: "message_date", label: "Message Date", description: "Date/Time the message was sent" },
                { value: "message", label: "Message", description: "Content of the message" },
                { value: "direction", label: "Direction", description: "Message direction (Inbound/Outbound)" },
                { value: "line", label: "Line", description: "Phone line used" },
                { value: "from", label: "From", description: "Sender information" },
                { value: "to", label: "To", description: "Message recipient" },
                { value: "attachments", label: "Attachments", description: "Attached files" },
                { value: "tags", label: "Tags", description: "Tags associated with the message" },
                { value: "notes", label: "Notes", description: "Additional notes" },
                { value: "source", label: "Source", description: "The source of the message" },
            ];
        },
        callSubjectFields() {
            return [
                { value: "call_disposition", label: "Call Outcome (Disposition)", description: "Result/Outcome of the call" },
                { value: "from", label: "From", description: "Indicates the assigned owner of a task. Who called (in call)" },
                { value: "communication_direction", label: "Communication Direction", description: "Communication direction Inbound or Outbound" },
                { value: "to", label: "To", description: "Who received the call or message" },
            ];
        },
        smsSubjectFields() {
            return [
                { value: "from", label: "From", description: "Indicates the assigned owner of a task. Who Sent the message (in SMS)" },
                { value: "communication_direction", label: "Communication Direction", description: "Communication direction Inbound or Outbound" },
                { value: "to", label: "To", description: "Who received the call or message" },
            ];
        },
        sortedCallSubjectFields: {
            get() {
                return this.getSortedFields(this.callSubjectFields, 'call')
            },
            set(value) {
                // Update the order when vuedraggable changes the array
                this.fieldOrders.call = value.map(field => field.value)
            }
        },
        sortedSmsSubjectFields: {
            get() {
                return this.getSortedFields(this.smsSubjectFields, 'sms')
            },
            set(value) {
                // Update the order when vuedraggable changes the array
                this.fieldOrders.sms = value.map(field => field.value)
            }
        },
        cdrAlowareFields() {
            return [
                { value: 'communication_time',      label: 'Exact Communication Time',         description: 'Exact date/time the communication occurred' },
                { value: 'recording_url',  label: 'Call Recording URL',      description: 'Link to the call recording' },
                { value: 'voicemail_url',  label: 'Voicemail Recording URL', description: 'Link to the voicemail recording' },
                { value: 'communication_id',  label: 'Communication ID', description: 'Unique Aloware identifier for the communication' },
            ]
        },
    },
    watch: {
        // Watch for changes in field orders and emit updates
        'fieldOrders.call': {
            handler(newOrder) {
                this.reorderSelectedFields('call')
            },
            deep: true
        },
        'fieldOrders.sms': {
            handler(newOrder) {
                this.reorderSelectedFields('sms')
            },
            deep: true
        }
    },
    methods: {
        getSortedFields(fields, type) {
            const order = this.fieldOrders[type]
            if (!order || order.length === 0) return fields
            
            // Sort fields based on the order array
            return [...fields].sort((a, b) => {
                const aIndex = order.indexOf(a.value)
                const bIndex = order.indexOf(b.value)
                if (aIndex === -1) return 1
                if (bIndex === -1) return -1
                return aIndex - bIndex
            })
        },
        onDragEnd(type) {
            // This method is called when drag and drop ends
            // The order is automatically updated through the computed setter
            console.log(`${type} fields reordered:`, this.fieldOrders[type])
            
            // Reorder the selected fields to match the new order
            this.reorderSelectedFields(type)
        },
        
        /**
         * Save the field order separately from the selection
         */
        reorderSelectedFields(type) {
            this.$set(this.model.settings.activities_settings, `${type}_order`, [...this.fieldOrders[type]])

            console.log(`Saved ${type} order under activities_settings:`, this.fieldOrders[type])
        },

        async fetchActivityFields() {
            this.isLoadingActivityFields = true
            try {
                const response = await axios.get('/api/v1/integration/salesforce/activity-fields')
                this.activityFields = response.data.fields || []
            } catch (e) {
                this.activityFields = []
            } finally {
                this.isLoadingActivityFields = false
            }
        },

        addCommunicationMapping() {
            this.model.settings.communication_property_mapping.push({ aloware_field: null, sf_field: null })
        },

        removeCommunicationMapping(index) {
            this.model.settings.communication_property_mapping.splice(index, 1)
        },

        /**
         * Returns Aloware CDR fields not already selected in other rows.
         */
        availableAlowareFields(currentIndex) {
            const used = this.model.settings.communication_property_mapping
                .filter((_, i) => i !== currentIndex)
                .map(r => r.aloware_field)
                .filter(Boolean)
            return this.cdrAlowareFields.filter(f => !used.includes(f.value))
        },

        /**
         * Returns Salesforce Task fields not already selected in other rows.
         */
        availableSfFields(currentIndex) {
            const used = this.model.settings.communication_property_mapping
                .filter((_, i) => i !== currentIndex)
                .map(r => r.sf_field)
                .filter(Boolean)
            return this.activityFields.filter(f => !used.includes(f.name))
        },
    }
}
</script>
<style scoped>
.vertical-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sortable-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.sortable-checkbox-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 2px 2px 8px;
    border: 1px solid transparent;
    border-radius: 4px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: white;
}

.sortable-checkbox-item:hover {
    border-color: #e4e7ed;
    background-color: #f5f7fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Clean drag handle - single arrow icon always visible */
.drag-handle {
    margin-left: 8px;
    opacity: 1;
    transition: all 0.2s ease;
    cursor: grab;
    padding: 6px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    min-height: 28px;
    border: 1px solid #c0c4cc;
    background-color: #f5f7fa;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.drag-handle:hover {
    background-color: #e4e7ed;
    transform: scale(1.1);
    border-color: #409eff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.drag-handle:active {
    cursor: grabbing;
    transform: scale(0.95);
}

.text-gray {
    color: #606266;
    font-size: 18px;
}

/* Ensure checkboxes maintain their styling */
.sortable-checkbox-item .el-checkbox {
    flex: 1;
    margin-right: 0;
}

/* vuedraggable specific classes */
.ghost {
    opacity: 0.5;
    background: #c8ebfb;
    border: 2px dashed #409eff;
}

.chosen {
    background: #f0f9ff;
    border-color: #409eff;
    transform: scale(1.02);
}

.dragging {
    opacity: 0.8;
    transform: rotate(2deg);
    z-index: 1000;
}

/* Smooth transitions for all interactive elements */
.sortable-checkbox-item * {
    transition: all 0.2s ease;
}

/* Make SF field options display label + type tag inline */
.el-select-dropdown__item {
    display: flex;
    align-items: center;
}
</style>
